import React, { useEffect, useState, useMemo } from 'react';
import { Pencil, Trash2, MoreVertical, Search, Plus } from 'lucide-react';
import useComponentVisible from '../UI/ComponentVisible';
import { useUserContext } from '../../contexts/UserContext';
import { getOpenRouterWorkspaces } from '../../utils/APIs';
import WorkspaceModal from './WorkspaceModal';
import DeleteWorkspaceModal from './DeleteWorkspaceModal';
import { Button, Input, AgTableCard } from '../UI';

const ManageWorkspaces = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const { userData } = useUserContext();
    const [gridApi, setGridApi] = useState(null);

    const fetchWorkspaces = async () => {
        try {
            setLoading(true);
            const res = await getOpenRouterWorkspaces(
                userData?.token,
                userData?.selectedOrganization?.org_token
            );
            if (res?.status && Array.isArray(res.data)) {
                setWorkspaces(res.data);
            }
        } catch (error) {
            console.error('Error fetching OpenRouter workspaces:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkspaces();
    }, []);

    const colDefs = [
        {
            headerName: 'Organization',
            field: 'name',
            filter: true,
            sortable: true,
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="text-left font-medium text-primary hover:text-primary-hover hover:underline cursor-pointer"
                    onClick={() => {
                        setSelectedWorkspace(data);
                        setSelectedAction('edit');
                    }}
                >
                    {data?.name || data?.org_id}
                </button>
            ),
            suppressMovable: true,
        },
        {
            headerName: 'Org ID',
            field: 'org_id',
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <span className="font-mono text-xs px-2 py-0.5 bg-surface-alt rounded border border-border-subtle text-stride-muted">
                    {data?.org_id}
                </span>
            ),
            suppressMovable: true,
        },
        {
            headerName: 'Workspace Name',
            field: 'workspace_name',
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <span className="font-medium text-xs text-stride-heading">
                    {data?.workspace_name}
                </span>
            ),
            suppressMovable: true,
        },
        {
            headerName: 'API Key',
            field: 'workspace_key',
            cellRenderer: ({ data }) => (
                <span className="font-mono text-xs px-2 py-0.5 bg-surface-alt rounded border border-border-subtle text-stride-muted tracking-wider">
                    {data?.workspace_key || '••••••••••••'}
                </span>
            ),
            suppressMovable: true,
        },
        {
            headerName: 'Status',
            field: 'is_active',
            sortable: true,
            cellRenderer: ({ data }) => {
                const isActive = data?.is_active !== false;
                return (
                    <span
                        className={`stride-badge-pill text-xs font-semibold ${
                            isActive
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                    >
                        <span
                            className={`w-1.5 h-1.5 mr-1.5 rounded-full ${
                                isActive ? 'bg-emerald-500' : 'bg-amber-500'
                            }`}
                        />
                        {isActive ? 'Active' : 'Inactive (Fallback)'}
                    </span>
                );
            },
            suppressMovable: true,
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 120,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => (
                <div className="stride-table-actions">
                    <button
                        type="button"
                        title="Edit Workspace"
                        className="stride-table-btn stride-table-btn-edit"
                        onClick={() => {
                            setSelectedWorkspace(data);
                            setSelectedAction('edit');
                        }}
                    >
                        <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        title="Delete Workspace"
                        className="stride-table-btn stride-table-btn-delete"
                        onClick={() => {
                            setSelectedWorkspace(data);
                            setSelectedAction('delete');
                        }}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        className="stride-table-btn actions"
                        title="More options"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsComponentVisible(!isComponentVisible);
                            setSelectedWorkspace(data);
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({
                                top: buttonRect.bottom + window.scrollY,
                                left: buttonRect.left + window.scrollX,
                            });
                        }}
                    >
                        <MoreVertical className="w-3.5 h-3.5" />
                    </button>
                </div>
            ),
        },
    ];

    const autoSizeStrategy = useMemo(() => {
        return {
            type: 'fitGridWidth',
            defaultMinWidth: 120,
            columnLimits: [
                { colId: 'name', minWidth: 180 },
                { colId: 'org_id', minWidth: 140 },
                { colId: 'workspace_name', minWidth: 160 },
                { colId: 'workspace_key', minWidth: 160 },
                { colId: 'is_active', minWidth: 130 },
                { colId: 'actions', minWidth: 120 },
            ],
        };
    }, []);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = event.target.value;
        if (gridApi) {
            gridApi.setQuickFilter(filterValue);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <>
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Administration</span>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                                OpenRouter Workspace Assignments
                            </h1>
                            <span className="text-xs text-stride-muted font-normal">
                                {workspaces.length} {workspaces.length === 1 ? 'assignment' : 'assignments'}
                            </span>
                        </div>
                        <p className="text-xs text-stride-muted mt-1">
                            Assign dedicated OpenRouter workspaces and API keys to organizations. Organizations without a custom workspace automatically use the system default key.
                        </p>
                    </div>

                    <Button
                        variant="primary"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={() => {
                            setSelectedWorkspace(null);
                            setSelectedAction('create');
                        }}
                    >
                        Assign Workspace
                    </Button>
                </div>

                <AgTableCard
                    header={
                        <div className="w-full sm:w-80">
                            <Input
                                placeholder="Search by Org, Workspace, or ID..."
                                onChange={onFilterTextBoxChanged}
                                prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                clearable
                            />
                        </div>
                    }
                    tableId="manage-openrouter-workspaces-table"
                    loading={loading}
                    rowData={workspaces}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 176) }}
                        >
                            <div className="py-1">
                                <a
                                    href="#"
                                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('edit');
                                    }}
                                >
                                    Edit Details
                                </a>
                                <a
                                    href="#"
                                    className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setIsComponentVisible(false);
                                        setSelectedAction('delete');
                                    }}
                                >
                                    Remove Workspace
                                </a>
                            </div>
                        </div>
                    )}
                </AgTableCard>
            </main>

            {/* Modals */}
            {selectedAction === 'create' && (
                <WorkspaceModal
                    setSelectedAction={setSelectedAction}
                    getWorkspaces={fetchWorkspaces}
                />
            )}
            {selectedAction === 'edit' && (
                <WorkspaceModal
                    setSelectedAction={setSelectedAction}
                    selectedWorkspace={selectedWorkspace}
                    getWorkspaces={fetchWorkspaces}
                />
            )}
            {selectedAction === 'delete' && (
                <DeleteWorkspaceModal
                    setSelectedAction={setSelectedAction}
                    selectedWorkspace={selectedWorkspace}
                    setSelectedWorkspace={setSelectedWorkspace}
                    getWorkspaces={fetchWorkspaces}
                />
            )}
        </>
    );
};

export default ManageWorkspaces;

